import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Upload, Library, Trophy, BarChart3, LogOut, Flame, Zap, ChevronsLeft, ChevronsRight, CreditCard, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Sidebar Usage Meter ──────────────────────────────────────────
function UsageMeter({ open }) {
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BACKEND}/api/billing/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setBilling(d))
      .catch(() => { });
  }, []);

  if (!billing) return null;

  const isPremium = billing.tier_name && billing.tier_name !== 'free';
  const pct = Math.min(100, Math.round(billing.percent_used || 0));
  const barColor = pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-amber-400' : 'bg-accent';

  if (!open) {
    // Collapsed: show just the plan icon hint
    return (
      <Link to="/settings/billing" title={`${billing.tier_display_name} — ${pct}% used`}
        className="flex justify-center py-2 px-2 rounded-xl hover:bg-white/10 transition-all">
        <CreditCard className={`w-5 h-5 ${isPremium ? 'text-violet-300' : 'text-white/50'}`} />
      </Link>
    );
  }

  return (
    <Link to="/settings/billing"
      data-testid="usage-meter-link"
      className="block rounded-xl bg-white/10 hover:bg-white/15 transition-all p-3 group">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full
            ${isPremium ? 'bg-violet-500/30 text-violet-200' : 'bg-white/15 text-white/60'}`}>
            {billing.tier_display_name}
          </span>
        </div>
        <ArrowRight className="w-3 h-3 text-white/40 group-hover:text-white/70 transition-colors" />
      </div>
      <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden mb-1.5">
        <motion.div
          className={`h-1.5 rounded-full ${barColor}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
      <p className="text-[10px] text-white/50">
        {billing.pages_consumed} / {billing.pages_limit} pages &bull; {pct}%
      </p>
      {!isPremium && (
        <p className="text-[10px] text-accent/80 mt-1 font-semibold group-hover:text-accent transition-colors">
          Upgrade for more →
        </p>
      )}
    </Link>
  );
}

// ─── Layout ───────────────────────────────────────────────────────
const Layout = ({ user, onLogout, refreshUser, children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navItems = [
    { path: '/', icon: Home, label: 'Dashboard', testId: 'nav-dashboard' },
    { path: '/upload', icon: Upload, label: 'Upload', testId: 'nav-upload' },
    { path: '/library', icon: Library, label: 'Library', testId: 'nav-library' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics', testId: 'nav-analytics' },
  ];

  const getLevelColor = (level) => {
    const colors = {
      'C-Student': 'text-gray-500',
      'B-Student': 'text-blue-500',
      'A-Student': 'text-green-500',
      'Scholar': 'text-purple-500',
      "Dean's List": 'text-yellow-500'
    };
    return colors[level] || 'text-gray-500';
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-primary text-primary-foreground transition-all duration-300 flex flex-col`}>
        <div className={`p-6 border-b border-white/10 flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
          <h1 className="text-2xl font-black tracking-tight">
            {sidebarOpen ? 'GradeGuard AI' : 'GG'}
          </h1>
        </div>

        {user && sidebarOpen && (
          <div className="p-4 border-b border-white/10 space-y-3">
            <div className="flex items-center gap-2" data-testid="user-level-display">
              <Trophy className={`w-5 h-5 ${getLevelColor(user.level)}`} />
              <span className="text-sm font-semibold font-ui">{user.level}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="user-xp-display">
              <Zap className="w-5 h-5 text-accent" />
              <span className="text-sm font-ui">{user.xp} XP</span>
            </div>
            <div className="flex items-center gap-2" data-testid="user-streak-display">
              <Flame className="w-5 h-5 text-warning" />
              <span className="text-sm font-ui">{user.streak} Day Streak</span>
            </div>
          </div>
        )}

        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex items-center ${sidebarOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-xl transition-all ${isActive
                  ? 'bg-accent text-white shadow-lg'
                  : 'hover:bg-white/10 text-primary-foreground/80 hover:text-primary-foreground'
                  }`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <item.icon className="w-5 h-5" />
                {sidebarOpen && <span className="font-medium font-ui">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Usage Meter */}
        <div className="px-4 pb-2">
          <UsageMeter open={sidebarOpen} />
        </div>

        <div className="p-4 space-y-2 border-t border-white/10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-collapse-toggle"
            className={`flex items-center ${sidebarOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-xl hover:bg-white/10 transition-all w-full text-left text-primary-foreground/60 hover:text-primary-foreground`}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronsLeft className="w-5 h-5" /> : <ChevronsRight className="w-5 h-5" />}
            {sidebarOpen && <span className="font-medium font-ui text-sm">Collapse</span>}
          </button>

          <button
            onClick={onLogout}
            data-testid="logout-button"
            className={`flex items-center ${sidebarOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-xl hover:bg-white/10 transition-all w-full text-left text-primary-foreground/80 hover:text-primary-foreground`}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium font-ui">Logout</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default Layout;